/**
 * The Mixpanel event each important route fires when it is opened.
 *
 * Every navigation used to fire ONE catch-all `Route` event carrying the page
 * in a property, which meant every funnel, every retention report and every
 * alert had to start by filtering that single event down to the page it cared
 * about. A converter page is a product of its own, so it now reports as one:
 * `/tools/hex-to-rgb` fires `HEX to RGB Viewed`, and a report about that
 * converter needs no filter at all.
 *
 * Keys are the NAVIGATIONS `url` of the route (the same string the SEO service
 * resolves a path to, so `/tools/hex-to-rgb/549eff` and `/tools/hex-to-rgb`
 * both land on `hex-to-rgb`). Names follow the object-then-past-tense-verb
 * shape the hand-written events already use (`Theme Expanded`, `Palette
 * Downloaded`), and acronyms stay upper-case so they read the way the pages
 * themselves are titled.
 *
 * A route that is NOT listed here fires no page event: auth, legal, OAuth
 * callbacks, the account area and the internal asset workbenches (/line,
 * /pinterest, /screenshots) are not funnels anyone reports on. Add a route here
 * the moment it becomes one.
 */
export const PAGE_EVENTS: { [ url: string ]: string } = {

  // Landing
  '': 'Home Viewed',
  'not-found': 'Not Found Viewed',

  // Themes
  'themes': 'Themes Viewed',
  'theme': 'Theme Viewed',

  // Palettes
  'palettes': 'Palettes Viewed',

  // Tools
  'tools': 'Tools Viewed',
  'color': 'Color Tool Viewed',
  'gradient': 'Gradient Tool Viewed',
  'wallpaper': 'Wallpaper Tool Viewed',
  'dynamic-wallpaper': 'Dynamic Wallpaper Tool Viewed',
  'code-snippet': 'Code Snippet Tool Viewed',
  'contrast-checker': 'Contrast Checker Viewed',
  'color-blindness-simulator': 'Color Blindness Simulator Viewed',

  'ai-cost-calculator': 'AI Cost Calculator Viewed',
  'ai-price-watch': 'AI Price Watch Viewed',

  // Color converters, one event per direction
  'hex-to-rgb': 'HEX to RGB Viewed',
  'rgb-to-hex': 'RGB to HEX Viewed',
  'hex-to-hsl': 'HEX to HSL Viewed',
  'hsl-to-hex': 'HSL to HEX Viewed',
  'hex-to-hsv': 'HEX to HSV Viewed',
  'hsv-to-hex': 'HSV to HEX Viewed',
  'hex-to-hsb': 'HEX to HSB Viewed',
  'hsb-to-hex': 'HSB to HEX Viewed',
  'hex-to-hwb': 'HEX to HWB Viewed',
  'hwb-to-hex': 'HWB to HEX Viewed',
  'hex-to-cmyk': 'HEX to CMYK Viewed',
  'cmyk-to-hex': 'CMYK to HEX Viewed',
  'hex-to-oklch': 'HEX to OKLCH Viewed',
  'oklch-to-hex': 'OKLCH to HEX Viewed',
  'hex-to-oklab': 'HEX to OKLAB Viewed',
  'oklab-to-hex': 'OKLAB to HEX Viewed',
  'hex-to-cielab': 'HEX to CIELAB Viewed',
  'cielab-to-hex': 'CIELAB to HEX Viewed',
  'hex-to-cielch': 'HEX to CIELCH Viewed',
  'cielch-to-hex': 'CIELCH to HEX Viewed',
  'hex-to-cieluv': 'HEX to CIELUV Viewed',
  'cieluv-to-hex': 'CIELUV to HEX Viewed',
  'hex-to-display-p3': 'HEX to Display P3 Viewed',
  'display-p3-to-hex': 'Display P3 to HEX Viewed',
  'hex-to-xyz': 'HEX to XYZ Viewed',
  'xyz-to-hex': 'XYZ to HEX Viewed',
  'hex-to-yxy': 'HEX to Yxy Viewed',
  'yxy-to-hex': 'Yxy to HEX Viewed',
  'hex-to-hunter-lab': 'HEX to Hunter Lab Viewed',
  'hunter-lab-to-hex': 'Hunter Lab to HEX Viewed',

  // Apps, plugins and extensions
  'plugins': 'Plugins Viewed',
  'chrome/color-picker': 'Chrome Color Picker Viewed',
  'chrome/all-in-one-themes': 'All In One Themes Viewed',
  'jetbrains/color-picker': 'JetBrains Color Picker Viewed',
  'vscode/color-picker': 'VS Code Color Picker Viewed',
  'macos/color-picker': 'MacOS Color Picker Viewed',
  'macos/tidy-desktop': 'MacOS Tidy Desktop Viewed',
  'macos/dock-town': 'MacOS Dock Town Viewed',
  'macos/ai-token-meter': 'MacOS AI Token Meter Viewed',

  // Blog. The post page fires its own `Blog Post Viewed` with the post key, so
  // only the list is named here and `pageEventName` keeps a post from firing a
  // second, thinner event on top of it.
  'blog': 'Blog Viewed',

  // Services and company
  'software-consultancy': 'Software Consultancy Viewed',
  'software-integration': 'Software Integration Viewed',
  'quality-assurance': 'Quality Assurance Viewed',
  'contact-us': 'Contact Us Viewed',
  'branding': 'Branding Viewed',
};

/**
 * The human name of a page, used as the `from` property on the NEXT page's
 * event so a report reads "came from HEX to RGB" instead of "came from
 * hex-to-rgb". Mapped routes reuse their own event name without the verb;
 * unmapped ones (legal, auth, the account area) still need a readable label
 * because they can be the page someone came FROM, so their slug is title-cased.
 */
export function pageLabel( url: string, params: { [ key: string ]: string } ): string {
  const event = pageEventName( url, params );
  if ( event ) {
    return event.replace( / Viewed$/, '' );
  }
  const route = url || 'home';
  return route
    .replace( /[/\-_]+/g, ' ' )
    .trim()
    .replace( /\b\w/g, ( c ) => c.toUpperCase() );
}

/**
 * The event name for a route, or null when the route reports nothing.
 *
 * Two routes need their params to decide, because one url serves two products:
 * `/palette` is the palette GENERATOR on its own and a theme's palette DETAIL
 * once `?product=` is present, and `/blog` is the reading list until a `slug`
 * turns it into a post the post page already reports itself.
 */
export function pageEventName( url: string, params: { [ key: string ]: string } ): string | null {
  const route = url || '';

  if ( route === 'palette' ) {
    return params[ 'product' ] ? 'Palette Detail Viewed' : 'Palette Generator Viewed';
  }
  if ( route === 'blog' ) {
    return params[ 'slug' ] ? null : PAGE_EVENTS[ 'blog' ];
  }
  return PAGE_EVENTS[ route ] || null;
}
